import { useCallback, useEffect, useRef, useState } from 'react'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import {
  deleteOperatingCostCampaignPixFee,
  deleteOperatingCostDefaultPixFee,
  fetchOperatingCostPixFees,
  upsertOperatingCostCampaignPixFee,
  upsertOperatingCostDefaultPixFee,
  type UpsertOperatingCostPixFeeInput,
} from '@/lib/api/operating-costs-admin.service'
import type { OperatingCostPixFeesPage } from '@/types/api'
import {
  OPERATING_COSTS_LOAD_ERROR,
  OPERATING_COSTS_PIX_CAMPAIGN_DELETED,
  OPERATING_COSTS_PIX_CAMPAIGN_SAVED,
  OPERATING_COSTS_PIX_DEFAULT_DELETED,
  OPERATING_COSTS_PIX_DEFAULT_SAVED,
  resolveOperatingCostPixFeesPage,
  resolveOperatingCostsError,
} from '../lib/operating-costs'

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true
  if (error instanceof Error && (error.name === 'AbortError' || /abort/i.test(error.message))) {
    return true
  }
  return false
}

export type PixFeesController = {
  data: OperatingCostPixFeesPage | null
  isLoading: boolean
  isSaving: boolean
  error: string | null
  success: string | null
  saveDefault: (input: UpsertOperatingCostPixFeeInput) => Promise<boolean>
  deleteDefault: () => Promise<boolean>
  saveCampaign: (campaignId: number, input: UpsertOperatingCostPixFeeInput) => Promise<boolean>
  deleteCampaign: (campaignId: number) => Promise<boolean>
}

type UsePixFeesOptions = {
  onSaved?: () => Promise<void> | void
}

export function usePixFees(options: UsePixFeesOptions = {}): PixFeesController {
  const { runWithStepUp } = useCampaignTotpStepUp()
  const runWithStepUpRef = useRef(runWithStepUp)
  runWithStepUpRef.current = runWithStepUp
  const onSavedRef = useRef(options.onSaved)
  onSavedRef.current = options.onSaved
  const [data, setData] = useState<OperatingCostPixFeesPage | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const loadControllerRef = useRef<AbortController | null>(null)
  const isSavingRef = useRef(false)

  const load = useCallback(async (silent = false) => {
    loadControllerRef.current?.abort()
    const controller = new AbortController()
    loadControllerRef.current = controller
    if (!silent) {
      setIsLoading(true)
    }
    setError(null)
    try {
      const response = await runWithStepUpRef.current(() => fetchOperatingCostPixFees(controller.signal))
      if (controller.signal.aborted) return
      const resolved = resolveOperatingCostPixFeesPage(response)
      if (!resolved) {
        setData(null)
        setError(OPERATING_COSTS_LOAD_ERROR)
        return
      }
      setData(resolved)
    } catch (err) {
      if (controller.signal.aborted || isAbortError(err)) return
      setData(null)
      setError(resolveOperatingCostsError(err))
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    void load()
    return () => {
      loadControllerRef.current?.abort()
    }
  }, [load])

  const runMutation = useCallback(
    async (action: () => Promise<unknown>, successMessage: string): Promise<boolean> => {
      if (isSavingRef.current) {
        return false
      }
      isSavingRef.current = true
      setIsSaving(true)
      setError(null)
      setSuccess(null)
      try {
        await runWithStepUpRef.current(action)
        setSuccess(successMessage)
        await load(true)
        await onSavedRef.current?.()
        return true
      } catch (err) {
        if (isAbortError(err)) return false
        setError(resolveOperatingCostsError(err))
        return false
      } finally {
        isSavingRef.current = false
        setIsSaving(false)
      }
    },
    [load]
  )

  return {
    data,
    isLoading,
    isSaving,
    error,
    success,
    saveDefault: (input) => runMutation(() => upsertOperatingCostDefaultPixFee(input), OPERATING_COSTS_PIX_DEFAULT_SAVED),
    deleteDefault: () => runMutation(() => deleteOperatingCostDefaultPixFee(), OPERATING_COSTS_PIX_DEFAULT_DELETED),
    saveCampaign: (campaignId, input) =>
      runMutation(() => upsertOperatingCostCampaignPixFee(campaignId, input), OPERATING_COSTS_PIX_CAMPAIGN_SAVED),
    deleteCampaign: (campaignId) =>
      runMutation(() => deleteOperatingCostCampaignPixFee(campaignId), OPERATING_COSTS_PIX_CAMPAIGN_DELETED),
  }
}
